import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, CalendarPlus, ExternalLink, Loader2, ArrowRight, CheckCircle2, ThumbsUp, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAIWorkspace } from "@/hooks/useAIWorkspace";
import { cn } from "@/lib/utils";
import { Fact } from "./parts";
import { fmtDay, fmtTime, fitTone, prepBullets } from "./portal";

export function OfferDetail({ a, onDone }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const o = a.offer || {};
  const respond = async (accept) => {
    setBusy(true);
    try {
      await api.post("/me/offers/respond", { candidate_id: a.id, accept });
      toast.success(accept ? "Offer accepted — congratulations!" : "Offer declined");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not respond"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid={`offer-${a.id}`}>
      <dl className="rounded-xl border border-primary/30 bg-primary/[0.06] p-5 grid sm:grid-cols-3 gap-4">
        <Fact label="Compensation" value={o.compensation || "—"} mono testId={`offer-comp-${a.id}`} />
        <Fact label="Start date" value={o.start_date || "—"} />
        <Fact label="Role" value={o.title || a.job?.title} />
      </dl>
      {o.message && <p className="text-sm leading-relaxed text-foreground/85 whitespace-pre-wrap">{o.message}</p>}
      <div className="flex flex-wrap items-center gap-2.5">
        <Button onClick={() => respond(true)} disabled={busy} data-testid={`accept-offer-${a.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}Accept offer</Button>
        {confirming
          ? <Button variant="outline" onClick={() => respond(false)} disabled={busy} className="text-rose-500 border-rose-500/40 hover:text-rose-500" data-testid={`confirm-decline-${a.id}`}>Confirm decline</Button>
          : <Button variant="ghost" onClick={() => setConfirming(true)} disabled={busy} className="text-rose-500 hover:text-rose-500" data-testid={`decline-offer-${a.id}`}>Decline</Button>}
      </div>
    </div>
  );
}

function bold(s) {
  return s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**")
    ? <strong key={i} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>
    : <span key={i}>{p.replace(/\*/g, "")}</span>));
}

export function InterviewDetail({ iv }) {
  const open = useAIWorkspace();
  const [prep, setPrep] = useState(iv.prep || "");
  const [busy, setBusy] = useState(false);
  const start = new Date(iv.when);
  const end = new Date(start.getTime() + (iv.duration_min || 60) * 6e4);
  const people = (iv.interviewers || []).filter((x) => !String(x).includes("@")).join(", ");
  const gen = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/me/interviews/${iv.id}/prep${prep ? "?force=true" : ""}`); setPrep(data.prep); }
    catch { toast.error("Couldn't generate prep notes"); }
    finally { setBusy(false); }
  };
  const lines = prepBullets(prep);
  return (
    <div className="space-y-4" data-testid={`myinterview-${iv.id}`}>
      <dl className="rounded-xl border border-sky-500/25 bg-sky-500/[0.05] p-5 grid sm:grid-cols-3 gap-4">
        <Fact label="When" value={`${fmtDay(iv.when)} · ${fmtTime(start)}–${fmtTime(end)}`} sub={Intl.DateTimeFormat().resolvedOptions().timeZone} />
        <Fact label="Where" value={iv.mode || "Video"} sub={iv.join_link ? "Join link below" : "Your interviewer will share details"} />
        <Fact label="With" value={people || "The hiring team"} />
      </dl>
      <div className="flex flex-wrap gap-2.5">
        {iv.ics_path && <Button asChild><a href={`${process.env.REACT_APP_BACKEND_URL}${iv.ics_path}`} data-testid={`ics-${iv.id}`}><CalendarPlus className="h-4 w-4 mr-1.5" />Add to calendar</a></Button>}
        {iv.join_link && <Button variant="outline" asChild><a href={iv.join_link} target="_blank" rel="noreferrer" data-testid={`myjoin-${iv.id}`}><ExternalLink className="h-4 w-4 mr-1.5" />Join link</a></Button>}
      </div>
      <section className="rounded-xl border border-border p-5" data-testid={`prep-${iv.id}`}>
        <header className="flex items-center justify-between gap-3 mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Prep notes for you</h3>
          <button onClick={gen} disabled={busy} className="text-[13px] text-primary hover:underline inline-flex items-center gap-1.5 disabled:opacity-60" data-testid={`prep-btn-${iv.id}`}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{prep ? "Regenerate" : "Generate prep notes"}
          </button>
        </header>
        {lines.length ? <ul className="space-y-1.5">{lines.map((l, i) => <li key={i} className="text-sm text-foreground/90 flex gap-2"><span className="mt-2 h-1 w-1 rounded-full bg-primary shrink-0" /><span>{bold(l)}</span></li>)}</ul>
          : <p className="text-sm text-muted-foreground">Get a checklist tailored to this role and your profile.</p>}
        <button onClick={() => open(`Run a practice interview with me for the ${iv.job_title} role.`, { previewUrl: "/candidate/applications" })}
          className="mt-3 text-[13px] text-primary hover:underline inline-flex items-center gap-1" data-testid={`practice-${iv.id}`}>Practise with Copilot <ArrowRight className="h-3.5 w-3.5" /></button>
      </section>
    </div>
  );
}

export function FeedbackDetail({ a, opps }) {
  const nav = useNavigate();
  const comps = [...(a.competencies || [])].filter((c) => c?.name).sort((x, y) => y.score - x.score);
  const strong = comps.filter((c) => c.score >= 70).slice(0, 2);
  const grow = comps.filter((c) => c.score < 70).slice(-2).reverse();
  const alt = (opps || []).filter((o) => !o.applied && (o.fit || 0) >= 45).slice(0, 2);
  return (
    <div className="space-y-4" data-testid={`feedback-${a.id}`}>
      <p className="text-[15px] leading-relaxed text-foreground/85">{a.company_name || "The employer"} decided not to move forward. A person reviewed your application{comps.length ? " — here's what your assessment showed:" : "."}</p>
      {(strong.length > 0 || grow.length > 0) && (
        <ul className="rounded-xl border border-border p-4 space-y-2.5">
          {strong.map((c) => <li key={c.name} className="flex gap-2.5 text-sm"><ThumbsUp className="h-4 w-4 text-primary shrink-0 mt-0.5" />Strong {c.name} ({Math.round(c.score)})</li>)}
          {grow.map((c) => <li key={c.name} className="flex gap-2.5 text-sm"><TrendingUp className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />Room to grow in {c.name} ({Math.round(c.score)})</li>)}
        </ul>
      )}
      {alt.length > 0 && (
        <div>
          <div className="text-sm text-muted-foreground mb-2.5">Roles you could fit next</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {alt.map((o) => (
              <button key={o.job.id} onClick={() => nav(`/candidate/jobs?job=${o.job.id}`)} className="hs-card p-4 text-left hover:border-foreground/20 transition-colors" data-testid={`feedback-alt-${o.job.id}`}>
                <div className="font-semibold text-sm truncate">{o.job.title}</div>
                <div className="text-[13px] text-muted-foreground">{o.company_name} · <span className={cn("tabular-nums", fitTone(o.fit))}>{Math.round(o.fit)}%</span></div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
